import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { type BackgroundVariant } from './background-layout';

type BackgroundConfig = {
  opacity?: string;
  visible: boolean;
  variant?: BackgroundVariant;
  ellipse1Variant?: BackgroundVariant;
  ellipse2Variant?: BackgroundVariant;
};

type BackgroundContextValue = {
  config: BackgroundConfig;
  setBackground: (next: Partial<BackgroundConfig>) => void;
  resetBackground: () => void;
};

const defaultConfig: BackgroundConfig = {
  visible: false,
};

const BackgroundContext = createContext<BackgroundContextValue | null>(null);

const BackgroundProvider = ({ children }: { readonly children: ReactNode }) => {
  const [config, setConfig] = useState<BackgroundConfig>(defaultConfig);

  const setBackground = useCallback((next: Partial<BackgroundConfig>) => {
    setConfig((prev) => ({ ...prev, ...next }));
  }, []);

  const resetBackground = useCallback(() => {
    setConfig(defaultConfig);
  }, []);

  const value = useMemo<BackgroundContextValue>(
    () => ({
      config,
      setBackground,
      resetBackground,
    }),
    [config, resetBackground, setBackground]
  );

  return <BackgroundContext.Provider value={value}>{children}</BackgroundContext.Provider>;
};

const useBackground = () => {
  const ctx = useContext(BackgroundContext);
  if (!ctx) throw new Error('useBackground must be used inside BackgroundProvider');
  return ctx;
};

export type { BackgroundConfig };
export { BackgroundProvider, useBackground };
